import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { get, startCase } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useHistory } from "react-router-dom";

import { ConnectAccountErrorMessage } from "components";
import { CredentialsForm } from "containers";
import * as addRoyaltyAccountRoutes from "containers/AddRoyaltyAccount/routes";
import { FormValues } from "containers/CredentialsForm";
import { handleConnectAccount } from "services/royaltyAccount";
import { ErrorInfo } from "services/royaltyAccount/types";
import { useValidateRoyaltyAccountCredentials } from "services/validateRoyaltyAccountCredentialsTasks/validateCredentials";
import { getOrCreateValuation } from "services/valuation";

import { BaseConnectAccountProps } from "../..";
import { TimeForEstimation } from "../";

const Connect: FC<BaseConnectAccountProps> = ({
  royaltyPayor,
  ConnectAccountInstructions,
  credentialsCustomPlaceholders,
}) => {
  const history = useHistory();
  const posthog = usePostHog();
  const {
    isOnTwoFAStep,
    validateCredentials,
    validateCredentialsErrorInfo,
  } = useValidateRoyaltyAccountCredentials();
  const [
    connectionErrorInfo,
    setConnectionErrorInfo,
  ] = useState<ErrorInfo | null>(null);

  const errorInfo = validateCredentialsErrorInfo || connectionErrorInfo;

  const usernameOrEmailDisplay =
    royaltyPayor.username_or_email === "username"
      ? "username"
      : "email address";

  const handleValidate = async (values: FormValues) => {
    setConnectionErrorInfo(null);
    const success = await validateCredentials({
      username: values.username,
      password: values.password,
      royalty_payor: royaltyPayor.name,
      two_fa_code: values.twoFACode || "",
    });
    if (success) {
      const asset = await handleConnectAccount(
        values,
        null,
        royaltyPayor.name,
        setConnectionErrorInfo,
        posthog,
      );
      getOrCreateValuation(asset.id);
      history.push(
        addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccountSuccess(
          royaltyPayor.name,
        ),
      );
    }
  };

  return (
    <>
      {!!errorInfo && (
        <Alert severity="warning" icon={false}>
          <ConnectAccountErrorMessage
            errorInfo={errorInfo}
            royaltyPayor={royaltyPayor}
          />
        </Alert>
      )}
      <Box mb={2}>
        <TimeForEstimation royaltyPayorName={royaltyPayor.display_name} />
      </Box>
      {ConnectAccountInstructions ? (
        <ConnectAccountInstructions />
      ) : (
        <>
          {isOnTwoFAStep ? (
            <Typography color="primary" align="left">
              Enter the Two-Factor Authentication code sent to you by{" "}
              {royaltyPayor.display_name}.
            </Typography>
          ) : (
            <>
              <Typography color="primary" gutterBottom align="left">
                Connect your {royaltyPayor.display_name} account by entering
                your {usernameOrEmailDisplay} and password exactly as you would
                on the {royaltyPayor.display_name} site.
              </Typography>
              <Typography color="primary">
                You’ll give our system temporary access to your{" "}
                {royaltyPayor.display_name} account where it can read statements
                and provide an analysis of your royalties.
              </Typography>
            </>
          )}
        </>
      )}
      <CredentialsForm
        royaltyPayor={royaltyPayor}
        onSubmit={handleValidate}
        isOnTwoFAStep={isOnTwoFAStep}
        usernameProps={{
          placeholder: get(
            credentialsCustomPlaceholders,
            "username",
            `${royaltyPayor.display_name} ${startCase(usernameOrEmailDisplay)}`,
          ),
        }}
        passwordProps={{
          placeholder: get(
            credentialsCustomPlaceholders,
            "password",
            `${royaltyPayor.display_name} Password`,
          ),
        }}
        initialValues={{
          username: "",
          password: "",
          twoFACode: "",
        }}
      />
    </>
  );
};

export default Connect;
